import React from 'react';
import { useHistory } from 'react-router-dom';
import { withErrorBoundary } from 'react-error-boundary';
import { useTranslation, Trans } from 'react-i18next';
import { Button, Typography } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { Mastercode, MastercodeStatus } from 'types/schema-generated-types';
import { AnnouncementPanel, AnnouncementPanelHeader } from '../AnnouncementPanel';

interface MastercodeUnlockAnnouncementProps {
  mastercodes?: Mastercode[];
  href?: string;
  onActivateOrTransfer?: React.MouseEventHandler<HTMLButtonElement>;
}

const isLocked = (mastercode: Mastercode) =>
  mastercode.status &&
  [MastercodeStatus.LOCKED, MastercodeStatus.SCHEDULED].includes(mastercode.status);

const MastercodeUnlockAnnouncement = ({
  mastercodes = [],
  href,
  onActivateOrTransfer
}: MastercodeUnlockAnnouncementProps) => {
  const { t } = useTranslation('MastercodeUnlockAnnouncement');
  const history = useHistory();

  const onButtonClick = href ? () => history.push(href) : onActivateOrTransfer;

  const lockedMastercodeCount = mastercodes.filter(isLocked).length;
  const show = lockedMastercodeCount > 0;

  return (
    <AnnouncementPanel
      show={show}
      Header={
        <AnnouncementPanelHeader>
          <Typography variant="h2">
            <Trans t={t} i18nKey="mastercodesToUnlock" count={lockedMastercodeCount}>
              You have <strong>{{ count: lockedMastercodeCount }} new activation code</strong> ready to
              begin your setup!
            </Trans>
          </Typography>
        </AnnouncementPanelHeader>
      }
      Actions={
        <Button
          variant="contained"
          color="primary"
          disableElevation
          aria-label={t('Activate or Transfer')}
          startIcon={<CheckIcon />}
          onClick={onButtonClick}
        >
          {t('Activate or Transfer')}
        </Button>
      }
    >
      <Typography variant="body1" color="textSecondary">
        {t(
          'You can activate or transfer your codes to a different administrator. Click the “Activate or Transfer" button on the right to start the process.'
        )}
      </Typography>
    </AnnouncementPanel>
  );
};

export default withErrorBoundary(MastercodeUnlockAnnouncement, { fallback: null });
